import { FC, MutableRefObject, useEffect, useMemo, useState } from 'react';
import { PreloadedQuery, usePreloadedQuery } from 'react-relay';
import ExitSlipModalBodyData from './ExitSlipModalBodyData';
import { ExitSlipRowVariant } from './ExitSlipLessonRow';
import ExitSlipModalTabBar, { ExitSlipTab } from './ExitSlipModalTabBar';
import { ExitSlipCurriculumLessonsQuery } from '../../graphQLOperations/__generated__/ExitSlipCurriculumLessonsQuery.graphql';
import { getClassExitSlipDueOnByClasses, getCurriculumLessonsByFilter } from '../../graphQLOperations/ExitSlip';
import { LessonItem } from './ExitSlipModalAssignBodyQuery';
import { LessonDate } from './ExitSlipModalContainer';
import { useAppSelector } from '../../redux/hooks';
import { selectedExitSlipFilter } from '../../redux/reducers/filterExitSlipSlice';
import { format, parseISO } from 'date-fns';
import {
  getDayClaims,
  getPassedUpcomingLessons,
  getStagedDayClaims,
  splitTodayUpcoming,
} from '../../businessLogicService/exitSlipSnapshotHelper';
import { Toast } from 'primereact/toast';
import { ExitSlipClassExitSlipDueOnByClassesQuery } from '../../graphQLOperations/__generated__/ExitSlipClassExitSlipDueOnByClassesQuery.graphql';

interface Props {
  queryReference: PreloadedQuery<ExitSlipCurriculumLessonsQuery, Record<string, unknown>>;
  queryDisabledDatesReference: PreloadedQuery<ExitSlipClassExitSlipDueOnByClassesQuery, Record<string, unknown>>;
  setNoDataYet: (d: boolean) => void;
  lessonDates: LessonDate[];
  setLessonDates: (a: LessonDate[]) => void;
  setSaveButtonDisabled: (s: boolean) => void;
  toast: MutableRefObject<Toast | null>;
  setApplyRefetchSnapshot: (s: boolean) => void;
  refreshPanel: () => void;
}

const sectionHeading = 'tw-m-0 tw-font-sora tw-text-xl tw-font-bold tw-leading-7 tw-text-neutral-950';
const subHeading = 'tw-m-0 tw-font-atkinson tw-text-base tw-font-bold tw-leading-5 tw-text-neutral-950';

const ExitSlipModalEditBodyQuery: FC<Props> = ({
  queryReference,
  queryDisabledDatesReference,
  setNoDataYet,
  lessonDates,
  setLessonDates,
  setSaveButtonDisabled,
  toast,
  setApplyRefetchSnapshot,
  refreshPanel,
}) => {
  const exitSlipFilterRedux = useAppSelector(selectedExitSlipFilter);

  const [activeTab, setActiveTab] = useState<ExitSlipTab>(ExitSlipTab.TODAY_AND_UPCOMING);
  const [passedLessons, setPassedLessons] = useState<LessonItem[]>([]);
  const [upcommingLessons, setUpcommingLessons] = useState<LessonItem[]>([]);

  const data = usePreloadedQuery<ExitSlipCurriculumLessonsQuery>(getCurriculumLessonsByFilter, queryReference);
  const disabledDatesData = usePreloadedQuery<ExitSlipClassExitSlipDueOnByClassesQuery>(
    getClassExitSlipDueOnByClasses,
    queryDisabledDatesReference,
  );

  useEffect(() => {
    if (!exitSlipFilterRedux.classes.length) {
      setNoDataYet(true);
      return;
    }

    const { passedLessons, upcomingLessons } = getPassedUpcomingLessons(data, exitSlipFilterRedux.classes);

    const toRow = (el: (typeof upcomingLessons)[number]): LessonItem => {
      const canceled = !!el.classExitSlips?.canceledOn;
      const dueOn = parseISO(el.classExitSlips?.dueOn as string);

      return {
        title: `Lesson ${el.number}: ${el.name}`,
        id: el.id,
        unit: el.unit,
        targetId: el.targetId,
        //TEAMB-3483 a canceled ticket holds no day — it reports the one it was called off from and
        //starts the picker empty, so re-assigning it reads as scheduling rather than moving
        date: canceled ? null : dueOn,
        canceledDate: canceled ? dueOn : null,
        classExitSlip: el.classExitSlips?.id,
        activityId: el.activityId,
      };
    };

    const upcomingRows = upcomingLessons.map(toRow);
    const passedRows = passedLessons.map(toRow);

    if (upcomingRows.length === 0 && passedRows.length) setActiveTab(ExitSlipTab.PAST);
    if (passedRows.length === 0 && upcomingRows.length) setActiveTab(ExitSlipTab.TODAY_AND_UPCOMING);

    setUpcommingLessons(upcomingRows);
    setPassedLessons(passedRows);

    //TEAMB-3422 a refresh rebuilds this list from the server, so carry over the picks already
    //staged against rows that survived — cancelling one ticket must not drop an edit on another
    const rowKey = (lessonId: string, slipId?: string | null) => `${lessonId}:${slipId ?? ''}`;
    const staged = new Map(
      lessonDates.filter(el => el.isEdited).map(el => [rowKey(el.curriculumLessonId, el.classExitSlipId), el]),
    );

    //TEAMB-2233 one flat list keyed by lesson keeps Past and Today & upcoming edits in step
    const nextLessonDates = [...upcomingRows, ...passedRows].map(
      el =>
        staged.get(rowKey(el.id, el.classExitSlip)) ?? {
          curriculumLessonId: el.id,
          date: el.date ?? null,
          classExitSlipId: el.classExitSlip as string,
          isReassinged: false,
          isEdited: false,
        },
    );

    setLessonDates(nextLessonDates);
    //Save was left lit after a refresh dropped the only staged edit, offering to submit nothing
    setSaveButtonDisabled(!nextLessonDates.some(el => el.isEdited));
    setNoDataYet(false);
  }, [data, exitSlipFilterRedux.classes]);

  //the query is roadmap-wide now that Unit is optional, so scope on the client when it is set
  const scopeToUnit = (rows: LessonItem[]) =>
    exitSlipFilterRedux.unit ? rows.filter(el => el.targetId === exitSlipFilterRedux.unit) : rows;

  const scopedUpcoming = useMemo(() => scopeToUnit(upcommingLessons), [upcommingLessons, exitSlipFilterRedux.unit]);
  const scopedPassed = useMemo(() => scopeToUnit(passedLessons), [passedLessons, exitSlipFilterRedux.unit]);

  const { todayLessons, upcomingLessons: laterLessons } = useMemo(
    () => splitTodayUpcoming(scopedUpcoming),
    [scopedUpcoming],
  );

  const { savedClaims, pastTags } = useMemo(
    () =>
      getDayClaims(
        disabledDatesData.classExitSlips,
        upcommingLessons.map(el => el.id),
      ),
    [disabledDatesData, upcommingLessons],
  );

  //canceled rows join the upcoming ones: a day picked for a re-assign has to block the other rows
  //too, or two of them stage onto it and the save comes back a unique violation. The rest of Past
  //is left out — those rows cannot be moved, and their days are already tagged by pastTags.
  const stagedClaims = useMemo(
    () => getStagedDayClaims([...upcommingLessons, ...passedLessons.filter(el => el.canceledDate)], lessonDates),
    [upcommingLessons, passedLessons, lessonDates],
  );

  return (
    <div className="tw-flex tw-flex-col">
      <div className="tw-sticky tw-top-0 tw-z-10 tw-bg-white tw-px-8 tw-py-4">
        <ExitSlipModalTabBar
          activeTab={activeTab}
          upcomingCount={scopedUpcoming.length}
          pastCount={scopedPassed.length}
          onChange={setActiveTab}
        />
      </div>

      {activeTab === ExitSlipTab.TODAY_AND_UPCOMING ? (
        <div className="tw-flex tw-flex-col tw-gap-6 tw-p-8">
          <div className="tw-flex tw-flex-col tw-gap-4">
            <div className="tw-flex tw-items-baseline tw-gap-3">
              <h3 className={sectionHeading}>Today&apos;s lesson</h3>
              <span className="tw-font-atkinson tw-text-base tw-leading-5 tw-text-[#767676]">
                {format(new Date(), 'MM/dd')}
              </span>
            </div>
            <ExitSlipModalBodyData
              variant={ExitSlipRowVariant.SCHEDULED}
              lessons={todayLessons}
              lessonDates={lessonDates}
              setLessonDates={setLessonDates}
              setSaveButtonDisabled={setSaveButtonDisabled}
              savedClaims={savedClaims}
              stagedClaims={stagedClaims}
              pastTags={pastTags}
              highlighted
              toast={toast}
              setApplyRefetchSnapshot={setApplyRefetchSnapshot}
              refreshPanel={refreshPanel}
              emptyState={
                <span className="tw-font-atkinson tw-text-base tw-leading-6 tw-text-neutral-500">
                  No exit ticket today
                </span>
              }
            />
          </div>

          <hr className="tw-m-0 tw-border-0 tw-border-t tw-border-solid tw-border-[#F1F0EE]" />

          <div className="tw-flex tw-flex-col tw-gap-4">
            <h4 className={subHeading}>Upcoming assigned lessons</h4>
            <ExitSlipModalBodyData
              variant={ExitSlipRowVariant.SCHEDULED}
              lessons={laterLessons}
              lessonDates={lessonDates}
              setLessonDates={setLessonDates}
              setSaveButtonDisabled={setSaveButtonDisabled}
              savedClaims={savedClaims}
              stagedClaims={stagedClaims}
              pastTags={pastTags}
              toast={toast}
              setApplyRefetchSnapshot={setApplyRefetchSnapshot}
              refreshPanel={refreshPanel}
              emptyState={
                <span className="tw-font-atkinson tw-text-base tw-leading-6 tw-text-neutral-500">
                  No upcoming exit tickets
                </span>
              }
            />
          </div>
        </div>
      ) : (
        <div className="tw-flex tw-flex-col tw-gap-6 tw-p-8">
          <h3 className={sectionHeading}>Past exit tickets</h3>
          <ExitSlipModalBodyData
            variant={ExitSlipRowVariant.PAST}
            lessons={scopedPassed}
            lessonDates={lessonDates}
            setLessonDates={setLessonDates}
            setSaveButtonDisabled={setSaveButtonDisabled}
            savedClaims={savedClaims}
            stagedClaims={stagedClaims}
            pastTags={pastTags}
            emptyState={
              <span className="tw-font-atkinson tw-text-base tw-leading-6 tw-text-neutral-500">
                No past exit tickets
              </span>
            }
          />
        </div>
      )}
    </div>
  );
};

export default ExitSlipModalEditBodyQuery;
